import { UUID } from '@litlingo/client';
import ScopesSection from 'components/LanguageMatcherRedesign/ScopesSection';
import { matcherTypeLabelMap } from 'constants/annotator';
import { bgColorsUtilization, textUtilization } from 'constants/rowItem';
import React from 'react';
import {
  getAnnotationMatchedLanguage,
  getAnnotationMatchedLanguageV2,
  getShowV2Graph,
} from 'selectors/communications';
import {
  getIdentifierShowUtilization,
  getMatcherUtilization,
  getOriginalMatcherUtilization,
  getScopedCustomersMatcher,
} from 'selectors/identifier';
import { useSelector } from 'store';
import type { MLanguageMatcher, NormalizedIdentifier } from 'types';

const formatMatcherType = (type: string): string => {
  const t = type || '';
  if (t.startsWith('metadata_')) {
    const sub = t.replace('metadata_', '').replaceAll('_', ' ');
    const subTitle = sub ? `${sub.charAt(0).toUpperCase()}${sub.slice(1)}` : '';
    return subTitle ? `Metadata (${subTitle})` : 'Metadata';
  }
  return (
    matcherTypeLabelMap[t as keyof typeof matcherTypeLabelMap] ||
    `${t.charAt(0).toUpperCase()}${t.slice(1)}`
  );
};

const getBgClass = (
  isSelected: boolean,
  showUtilization: boolean,
  utilization: keyof typeof bgColorsUtilization,
  selectedCustomer: boolean,
  isSelectedCustomerScoped: boolean,
  isMainBlock: boolean
): string => {
  if (isSelected) return 'bg-litlingo-success-light';
  if (selectedCustomer && isSelectedCustomerScoped) return 'bg-white';
  if (selectedCustomer && !isSelectedCustomerScoped && !isMainBlock) return 'bg-litlingo-gray-0.5';
  if (showUtilization) return bgColorsUtilization[utilization];
  if (showUtilization) return bgColorsUtilization[utilization];
  return 'bg-white';
};

type ComponentProps = {
  languageMatcher: MLanguageMatcher;
  highlightedLanguageMatcher: string | undefined;
  handleLanguageMatcherClick: (languageMatcherId: UUID) => void;
  identifierRevision: NormalizedIdentifier;
  selectedCustomer: string;
};

const LanguageMatcherSidebarItem: React.FC<ComponentProps> = ({
  languageMatcher,
  highlightedLanguageMatcher,
  handleLanguageMatcherClick,
  identifierRevision,
  selectedCustomer,
}) => {
  const utilization = useSelector((state) => getMatcherUtilization(state, languageMatcher.uuid));
  const originalUtilization = useSelector((state) =>
    getOriginalMatcherUtilization(state, languageMatcher.uuid)
  );
  const scopedCustomers = useSelector((state) =>
    getScopedCustomersMatcher(state, languageMatcher.uuid)
  );
  const showV2 = useSelector(getShowV2Graph);

  const annotationsMatched = useSelector(getAnnotationMatchedLanguage);
  const annotationsMatchedV2 = useSelector(getAnnotationMatchedLanguageV2);

  const showUtilization = useSelector(getIdentifierShowUtilization);

  const isSelected = languageMatcher.uuid === highlightedLanguageMatcher;

  const formattedMatcherType = ((): string => {
    if ((languageMatcher.type || '') !== 'metadata_communication') {
      if (languageMatcher.parent) {
        return formatMatcherType('secondary');
      }
      return formatMatcherType(languageMatcher.type || '');
    }

    const variant = languageMatcher.__metadata_variant as string | undefined;
    const isGeneral = variant === 'general';
    return isGeneral ? 'Metadata (General)' : 'Metadata (Communication)';
  })();

  const isSelectedCustomerScoped = scopedCustomers.includes(selectedCustomer);
  // @ts-ignore
  const isMainBlock = identifierRevision.kind === 'llm_categorizer' && !languageMatcher.parent;

  const bgClass = getBgClass(
    isSelected,
    showUtilization,
    utilization,
    !!selectedCustomer,
    isSelectedCustomerScoped,
    isMainBlock
  );

  return (
    <div
      className={`identifier-card-hover relative flex flex-col border-b border-litlingo-gray-1  overflow-visible hover:z-20 ${
        isSelected ? 'z-10' : 'z-0 '
      }`}
    >
      <div
        key={languageMatcher.uuid}
        aria-hidden="true"
        className={` relative px-4 flex flex-row ${bgClass} overflow-visible `}
        onClick={(): void => handleLanguageMatcherClick(languageMatcher.uuid)}
      >
        <div
          className={`w-full relative py-2 flex flex-row justify-between cursor-pointer overflow-hidden ${
            languageMatcher.parent ? 'pl-4' : ''
          }`}
        >
          <div className="flex flex-col gap-1 text-body">
            <span
              className={`truncate ${isSelected ? 'font-bold' : ''} ${
                (showV2 && annotationsMatchedV2.includes(languageMatcher.name)) ||
                (!showV2 && annotationsMatched.includes(languageMatcher.name))
                  ? 'bg-litlingo-highlight'
                  : ''
              }`}
            >
              {languageMatcher.is_exclusion ? '-' : ''}
              <p
                className={`${
                  languageMatcher.enabled !== undefined && !languageMatcher.enabled
                    ? 'line-through'
                    : ''
                }`}
              >
                {languageMatcher.name}
              </p>
            </span>
            <span className="text-small">{formattedMatcherType}</span>
          </div>

          {/* @ts-ignore */}
          {(identifierRevision.kind !== 'llm_categorizer' || languageMatcher.parent) && (
            <div className="h-full flex flex-col justify-between text-body">
              <span className="font-bold text-right">{textUtilization[utilization]}</span>
              {utilization !== originalUtilization && (
                <span className="text-small text-right">
                  {textUtilization[originalUtilization]}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
      {/* @ts-ignore */}
      {(identifierRevision.kind !== 'llm_categorizer' || languageMatcher.parent) &&
        languageMatcher.uuid === highlightedLanguageMatcher && (
          <ScopesSection languageMatcher={languageMatcher} />
        )}

      {languageMatcher.uuid === highlightedLanguageMatcher && (
        <div className="absolute left-0 top-0 bottom-0 z-20">
          <svg
            width="5"
            height="100%"
            viewBox="0 0 5 64"
            fill="none"
            preserveAspectRatio="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect width="5" height="64" fill="#007656" />
          </svg>
        </div>
      )}
    </div>
  );
};

export default LanguageMatcherSidebarItem;
